import { Box, Form, Flex, Select, Button, Input } from '@cowsquare/design';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import defaultCover from '../../../asset/defaultCover.jpg';
import { Section } from '../../../components/Section';
import { getQueryObject } from '../../../utils/getQueryObject';
import { useAdsQuery, usePagesBindingUrl } from '../../../utils/hooks';
import { Advertisements } from '../../Advertisements.js';
import { MedicalCardListLoading } from '../../Loading';
import MedicalCardList from '../../MedicalCardList.js';
import { Title } from '../../Title.js';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Form as FinalForm, Field } from 'react-final-form';

const PAGE_SIZE = 21;

const GET_HOSPITALS = gql`
  query getHospitals($limit: Int, $offset: Int, $where: hkdutest_hospitals_bool_exp) {
    total: hkdutest_hospitals_aggregate {
      aggregate {
        count
      }
    }

    list: hkdutest_hospitals(limit: $limit, order_by: { created_at: desc }, offset: $offset, where: $where) {
      id
      name
      cover {
        name
        link
      }
      phone
      address
    }
  }
`;

const HospitalFilterBox = styled(Box)`
  background-color: ${themeGet('colors.whites.0')};
`;

const composeLikeSearch = value => ({ _ilike: `%${value}%` });
const composeEqualSearch = value => ({ _eq: value });

const HospitalList = ({ location, uri }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const [whereClause, setWhereClause] = useState({});

  const typeOptions = [{ name: 'Public', value: true }, { name: 'Private', value: false }, { name: 'All' }];
  const emergencyServiceOptions = [
    { name: 'Available', value: true },
    { name: 'Not Available', value: false },
    { name: 'All' },
  ];

  return (
    <Query
      query={GET_HOSPITALS}
      variables={{
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        where: whereClause,
      }}
    >
      {({ data, loading }) => {
        const list = R.propOr([], 'list', data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);

        return (
          <Section>
            <Helmet>
              <title>Hospital - HKDU Public</title>
            </Helmet>
            <Advertisements ads={ads} />
            <Box px={[5, 0]}>
              <Title mt={8} mb={6}>
                Hospital
              </Title>
              <HospitalFilterBox p={5} mx={[-5, 0]} my={5}>
                <FinalForm
                  onSubmit={formValues => {
                    setWhereClause(
                      R.always(
                        R.pipe(
                          R.evolve({
                            public: R.prop('value'),
                            emergency_service: R.prop('value'),
                          }),
                          R.reject(R.isNil),
                          R.evolve({
                            public: composeEqualSearch,
                            emergency_service: composeEqualSearch,
                            name: composeLikeSearch,
                            address: composeLikeSearch,
                          })
                        )(formValues)
                      )
                    );
                  }}
                  render={({ handleSubmit, submitting }) => (
                    <Form>
                      <Flex flexDirection={['column', 'row']} justifyContent="space-between">
                        <Box flex={1} px={4}>
                          <Field name="public">
                            {({ input, meta }) => (
                              <Form.FormItem label="Type" input={input} meta={meta}>
                                <Select {...input} placeholder="全部" options={typeOptions} />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="address">
                            {({ input, meta }) => (
                              <Form.FormItem label="Address" input={input} meta={meta}>
                                <Input {...input} placeholder="輸入醫院地址搜尋" />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="emergency_service">
                            {({ input, meta }) => (
                              <Form.FormItem label="Emergency Service" input={input} meta={meta}>
                                <Select
                                  {...input}
                                  placeholder="Available"
                                  options={emergencyServiceOptions}
                                />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="name">
                            {({ input, meta }) => (
                              <Form.FormItem label="醫院名稱" input={input} meta={meta}>
                                <Input {...input} placeholder="輸入醫院名稱搜尋" />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                      </Flex>
                      <Box px={4}>
                        <Flex justifyContent="flex-end">
                          <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="primary"
                          >
                            Submit
                          </Button>
                        </Flex>
                      </Box>
                    </Form>
                  )}
                ></FinalForm>
              </HospitalFilterBox>
              {R.and(R.isEmpty(list), loading) && <MedicalCardListLoading hasCover />}
              <MedicalCardList
                height="364px"
                cardData={list}
                defaultCover={defaultCover}
                total={total}
                uri={uri}
                page={page}
                onPageChange={onPageChange}
              />
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default HospitalList;
